import { detectStorefront, getStorefrontConfig, type Storefront, type Currency } from '@/hooks/useStorefront';

/**
 * Formats a price amount with the correct currency for a storefront.
 * - CH (default): "CHF9.90"
 * - DE: "€9.90"
 *
 * 1:1 mapping — the same numeric value is shown, only the symbol changes.
 *
 * @param amount Numeric price (e.g. 9.9)
 * @param storefront Optional override. Defaults to current domain detection.
 * @param withSpace If true, inserts a space between symbol and number ("CHF 9.90").
 */
export function formatPrice(amount: number, storefront?: Storefront, withSpace = false): string {
  const sf = storefront ?? detectStorefront();
  const cfg = getStorefrontConfig(sf);
  const sep = withSpace ? ' ' : '';
  const value = (amount ?? 0).toFixed(2);
  if (sf === 'de') {
    // EUR: "€9.90"
    return `${cfg.currencySymbol}${sep}${value}`;
  }
  // CH: "CHF9.90"
  return `${cfg.currencySymbol}${sep}${value}`;
}

/**
 * Currency-aware formatter used by the display layer (useDisplayPrice).
 * CH stays byte-identical to formatPrice(amount) with no storefront arg.
 *   CHF -> "CHF9.90"
 *   EUR -> "€9,90"
 *   SEK -> "9,90 kr"
 *   ALL -> "9,90 L"
 */
export function formatPriceForCurrency(amount: number, currency: Currency): string {
  const n = amount ?? 0;
  switch (currency) {
    case 'CHF':
      return `CHF${n.toFixed(2)}`;
    case 'EUR':
      return `€${n.toFixed(2).replace('.', ',')}`;
    case 'SEK':
      return `${n.toFixed(2).replace('.', ',')} kr`;
    case 'ALL':
      return `${n.toFixed(2).replace('.', ',')} L`;
  }
}

/**
 * Returns just the currency symbol for the active (or specified) storefront.
 */
export function currencySymbol(storefront?: Storefront): string {
  const sf = storefront ?? detectStorefront();
  return getStorefrontConfig(sf).currencySymbol;
}

/**
 * Returns the ISO currency code (CHF / EUR) for the active (or specified) storefront.
 */
export function currencyCode(storefront?: Storefront): Currency {
  const sf = storefront ?? detectStorefront();
  return getStorefrontConfig(sf).currency;
}
